import { html } from '@design.estate/dees-element';

import { DeesUpdater } from '../dees-updater/dees-updater.js';

const waitForDemoStep = async (timeoutArg: number): Promise<void> => {
  await new Promise<void>((resolve) => {
    window.setTimeout(() => resolve(), timeoutArg);
  });
};

export const demoFunc = () => {
  let updaterRunning = false;

  const runDemoFlow = async (outcomeArg: 'success' | 'warning' | 'error') => {
    if (updaterRunning) {
      return;
    }

    updaterRunning = true;

    try {
      const updater = await DeesUpdater.createAndShow({
        appName: 'Dees Catalog',
        appIcon: 'lucide:packageCheck',
        currentVersion: '3.82.0',
        updatedVersion: '3.83.0',
        updateSource: 'code.foss.global',
        impactItems: [
          'The demo simulates a short application restart.',
          'Each phase shows only the latest status.',
          'The final step exposes a manual action while the countdown runs.',
        ],
        moreInfoUrl: 'https://code.foss.global/design.estate/dees-catalog',
        changelogUrl: 'https://code.foss.global/design.estate/dees-catalog/-/blob/main/changelog.md',
        successAction: 'close',
        successDelayMs: 10000,
        onRetry: async () => runDemoFlow('success'),
      });

      const progressFrames: Array<{
        phase: Parameters<typeof updater.setPhase>[0];
        line: string;
        delay: number;
      }> = [
        { phase: 'checking', line: 'Checking release manifest', delay: 5000 },
        { phase: 'downloading', line: 'Downloading signed bundle', delay: 5000 },
        { phase: 'verifying', line: 'Verifying checksum', delay: 5000 },
        { phase: 'installing', line: 'Applying update files', delay: 5000 },
        { phase: 'restarting', line: 'Restarting application service', delay: 5000 },
        { phase: 'verifyingApp', line: 'Waiting for application health check', delay: 5000 },
      ];

      for (const progressFrame of progressFrames) {
        updater.setPhase(progressFrame.phase, {
          line: progressFrame.line,
          statusText: `${progressFrame.line}...`,
        });
        await waitForDemoStep(progressFrame.delay);

        if (outcomeArg === 'error' && progressFrame.phase === 'installing') {
          updater.markUpdateError('The installer rejected the simulated update payload.');
          return;
        }

        if (outcomeArg === 'warning' && progressFrame.phase === 'verifyingApp') {
          updater.markUpdateWarning('The update command completed, but the simulated application health check timed out.');
          return;
        }
      }

      await updater.markUpdateReady();
      await waitForDemoStep(10500);
    } finally {
      updaterRunning = false;
    }
  };

  return html`
    <div style="display: grid; gap: 16px; place-items: center; padding: 32px; text-align: center;">
      <p style="margin: 0; max-width: 540px; line-height: 1.6; color: var(--dees-color-text-secondary);">
        Launches the updater as a full blackout install takeover: one calm centered column,
        a single continuous progress bar across all phases, a subtle phase checklist, a Details
        disclosure for the live log, and explicit success, warning, or error outcomes.
      </p>
      <div style="display: flex; gap: 10px; flex-wrap: wrap; justify-content: center;">
        <dees-button @click=${async () => runDemoFlow('success')}>Show success flow</dees-button>
        <dees-button @click=${async () => runDemoFlow('warning')}>Show warning flow</dees-button>
        <dees-button @click=${async () => runDemoFlow('error')}>Show error flow</dees-button>
      </div>
    </div>
  `;
};
